import { useCallback, useEffect, useRef, useState } from 'react';

type Drafts = Record<string, string>;

const SAVE_DELAY_MS = 400;

// Kept outside React so drafts survive leaving the Chat view (the workspace unmounts) and
// coming back before the server round trip has finished.
const cacheByUser = new Map<string, Drafts>();
// Chats whose latest local text the server has not confirmed yet; these win over server data.
const unconfirmedByUser = new Map<string, Set<string>>();

/**
 * Unsent composer text, one draft per chat, saved on the server per user.
 * Each chat reads and writes only its own entry, so a draft can never show up in another chat.
 */
export function useChatDrafts(token: string | null | undefined, userId: string | undefined) {
  const userKey = userId || 'anonymous';
  const [drafts, setDrafts] = useState<Drafts>(() => cacheByUser.get(userKey) || {});
  const [loaded, setLoaded] = useState(false);
  const draftsRef = useRef<Drafts>(drafts);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const authRef = useRef({ token, userId });
  authRef.current = { token, userId };

  const unconfirmed = () => {
    let set = unconfirmedByUser.get(userKey);
    if (!set) {
      set = new Set();
      unconfirmedByUser.set(userKey, set);
    }
    return set;
  };

  const save = useCallback(
    (channelId: string) => {
      const { token: t, userId: uid } = authRef.current;
      if (!t) return;
      fetch(`/api/chat/channels/${channelId}/draft`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${t}`,
          ...(uid ? { 'x-demo-user-id': uid } : {}),
        },
        body: JSON.stringify({ content: draftsRef.current[channelId] || '' }),
        keepalive: true, // lets the save finish even if the page or view goes away
      })
        .then((res) => {
          if (res.ok && !timersRef.current.has(channelId)) unconfirmedByUser.get(userKey)?.delete(channelId);
        })
        .catch((err) => console.warn('Could not save the message draft:', err));
    },
    [userKey]
  );

  const flush = useCallback(() => {
    timersRef.current.forEach((timer, channelId) => {
      clearTimeout(timer);
      timersRef.current.delete(channelId);
      save(channelId);
    });
  }, [save]);

  const setDraft = useCallback(
    (channelId: string, content: string) => {
      if ((draftsRef.current[channelId] || '') === content) return;
      const next = { ...draftsRef.current };
      if (content.trim()) next[channelId] = content;
      else delete next[channelId];
      draftsRef.current = next;
      cacheByUser.set(userKey, next);
      setDrafts(next);

      unconfirmed().add(channelId);
      clearTimeout(timersRef.current.get(channelId));
      timersRef.current.set(
        channelId,
        setTimeout(() => {
          timersRef.current.delete(channelId);
          save(channelId);
        }, SAVE_DELAY_MS)
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userKey, save]
  );

  const getDraft = useCallback((channelId: string | null) => (channelId ? draftsRef.current[channelId] || '' : ''), []);

  // Load the saved drafts after login
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch('/api/chat/drafts', {
      headers: { Authorization: `Bearer ${token}`, ...(userId ? { 'x-demo-user-id': userId } : {}) },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.drafts) return;
        const merged: Drafts = { ...data.drafts };
        unconfirmed().forEach((channelId) => {
          const local = draftsRef.current[channelId];
          if (local) merged[channelId] = local;
          else delete merged[channelId];
        });
        draftsRef.current = merged;
        cacheByUser.set(userKey, merged);
        setDrafts(merged);
      })
      .catch((err) => console.warn('Could not load message drafts:', err))
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId]);

  // Send any pending save when the view closes or the tab is hidden/closed
  useEffect(() => {
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [flush]);

  return { drafts, loaded, getDraft, setDraft };
}
